/**
 * Pure graph operations the builder page applies to a `BuilderGraph`.
 * Every op returns a new graph (inputs are never mutated).
 *
 * Structure rules:
 *  - one edge per output port: a node's default handle, or a named branch
 *    (`yes`, `no`, an option id, `success`, `error`), links to at most one node;
 *  - a node may be linked INTO from several places (branches rejoin);
 *  - nothing links into the trigger, nothing links to itself, and no loop is
 *    allowed (the engine has a step budget, but a loop is never what the
 *    author meant).
 */
import type { FlowEdgeData } from '@repletoryreach/sdk-ui/dnd';
import {
  TRIGGER_NODE_ID,
  TRIGGER_NODE_TYPE,
  LAYOUT,
  autoLayout,
  branchEdge,
  chainEdge,
  clone,
  layoutPosition,
  type BuilderGraph,
  type BuilderNode,
  type EngineNodeKind,
  type JsonObject
} from './mapper';
import { NODE_KINDS, outputsFor, type NodeKindSpec, intentEnabled, channelProblems, listMenu } from './components/catalog';

export type PaletteKind = EngineNodeKind | typeof TRIGGER_NODE_TYPE;

/** Sensible starting fields per kind: everything the engine reads has a default. */
export function defaultNodeData(kind: PaletteKind): JsonObject {
  if (kind === TRIGGER_NODE_TYPE) return { type: 'keyword', keyword: '' };
  const spec: NodeKindSpec | undefined = NODE_KINDS[kind];
  return clone(spec?.defaults ?? {});
}

export function hasTrigger(graph: BuilderGraph): boolean {
  return graph.nodes.some((n) => n.id === TRIGGER_NODE_ID);
}

/** Next free `n<k>` id. */
export function newNodeId(graph: BuilderGraph): string {
  const used = new Set(graph.nodes.map((n) => n.id));
  let k = 1;
  for (const id of used) {
    const m = /^n(\d+)$/.exec(id);
    if (m) k = Math.max(k, Number(m[1]) + 1);
  }
  while (used.has(`n${k}`)) k += 1;
  return `n${k}`;
}

function outgoingOf(graph: BuilderGraph, id: string): FlowEdgeData[] {
  return graph.edges.filter((e) => e.source === id);
}
function incomingOf(graph: BuilderGraph, id: string): FlowEdgeData[] {
  return graph.edges.filter((e) => e.target === id);
}

/** Ids reachable from `from` by following outgoing edges (excluding `from`). */
export function reachableFrom(graph: BuilderGraph, from: string): Set<string> {
  const seen = new Set<string>();
  const stack = [from];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const e of outgoingOf(graph, cur)) {
      if (seen.has(e.target)) continue;
      seen.add(e.target);
      stack.push(e.target);
    }
  }
  return seen;
}

/** The start: the trigger, else the first node without an incoming edge. */
export function chainStart(graph: BuilderGraph): string | null {
  if (hasTrigger(graph)) return TRIGGER_NODE_ID;
  const incoming = new Set(graph.edges.map((e) => e.target));
  return graph.nodes.find((n) => !incoming.has(n.id))?.id ?? graph.nodes[0]?.id ?? null;
}

/** Every node the engine can reach from the start, start included. */
export function chainFrom(graph: BuilderGraph, start: string | null): string[] {
  if (!start) return [];
  return [start, ...reachableFrom(graph, start)];
}

/**
 * A 1-based number per node, in the order a reader walks the flow: from the
 * trigger, the default path before the branches, then anything unreached in
 * the order it was added. The trigger itself carries no number.
 */
export function stepNumbers(graph: BuilderGraph): Map<string, number> {
  const out = new Map<string, number>();
  const seen = new Set<string>();
  const visit = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    if (id !== TRIGGER_NODE_ID) out.set(id, out.size + 1);
    const edges = outgoingOf(graph, id);
    for (const e of [...edges.filter((e) => !e.sourceHandle), ...edges.filter((e) => !!e.sourceHandle)]) visit(e.target);
  };
  const start = chainStart(graph);
  if (start) visit(start);
  for (const n of graph.nodes) visit(n.id);
  return out;
}

/** Nodes (other than the trigger) the engine would never reach. */
export function unlinkedNodeIds(graph: BuilderGraph): string[] {
  const start = chainStart(graph);
  const linked = new Set(chainFrom(graph, start));
  return graph.nodes.filter((n) => n.id !== TRIGGER_NODE_ID && !linked.has(n.id)).map((n) => n.id);
}

/**
 * Put a new node on an existing link: the link now goes source → new node →
 * old target. Everything from the old target down moves one row so nothing
 * overlaps and the author's own arrangement is otherwise kept.
 */
export function insertNode(graph: BuilderGraph, kind: PaletteKind, edgeId: string, data?: JsonObject): BuilderGraph {
  const edge = graph.edges.find((e) => e.id === edgeId);
  if (!edge || kind === TRIGGER_NODE_TYPE) return graph;
  const target = graph.nodes.find((n) => n.id === edge.target);
  const source = graph.nodes.find((n) => n.id === edge.source);
  if (!target || !source) return graph;
  const rowShift = LAYOUT.nodeHeight + LAYOUT.rowGap;
  const shifted: BuilderGraph = {
    ...graph,
    nodes: graph.nodes.map((n) => (n.position.y >= target.position.y && n.id !== source.id ? { ...n, position: { ...n.position, y: n.position.y + rowShift } } : n))
  };
  const withNode = appendNode(shifted, kind, data, { after: { node: edge.source, handle: edge.sourceHandle ?? undefined }, at: { x: target.position.x, y: target.position.y } });
  const id = withNode.nodes[withNode.nodes.length - 1]?.id;
  if (!id) return graph;
  return { ...withNode, edges: [...withNode.edges.filter((e) => e.id !== edgeId), chainEdge(id, target.id)] };
}

/** Take a node out of every link it has, keeping the node. */
export function detachNode(graph: BuilderGraph, id: string): BuilderGraph {
  const edges = graph.edges.filter((e) => e.source !== id && e.target !== id);
  return edges.length === graph.edges.length ? graph : { ...graph, edges };
}

/** The links into and out of a node, with the step each leads from or to. */
export function linksOf(graph: BuilderGraph, id: string): { incoming: Array<{ edge: BuilderGraph['edges'][number]; other: string }>; outgoing: Array<{ edge: BuilderGraph['edges'][number]; other: string }> } {
  return {
    incoming: graph.edges.filter((e) => e.target === id).map((e) => ({ edge: e, other: e.source })),
    outgoing: graph.edges.filter((e) => e.source === id).map((e) => ({ edge: e, other: e.target }))
  };
}

/** Lay every node out again from the structure ("Tidy up"). */
export function relayout(graph: BuilderGraph): BuilderGraph {
  return autoLayout(graph);
}

/**
 * The node a new node should hang off: the deepest reachable node that still
 * has a free default output and is not a branching kind. Null when there is
 * nowhere obvious, in which case the new node starts unlinked.
 */
export function chainTail(graph: BuilderGraph): string | null {
  const order = chainFrom(graph, chainStart(graph));
  for (let i = order.length - 1; i >= 0; i--) {
    const id = order[i];
    const node = graph.nodes.find((n) => n.id === id);
    if (!node) continue;
    if (node.id !== TRIGGER_NODE_ID && (outputsFor(node).length > 1 || node.type === 'end' || node.type === 'handoff')) continue;
    if (outgoingOf(graph, id).some((e) => !e.sourceHandle)) continue;
    return id;
  }
  return null;
}

export interface AppendOptions {
  /** Drop point in flow-space; omitted → laid out below the node it links from. */
  at?: { x: number; y: number };
  /** Link from this node (and port) instead of the chain's tail. */
  after?: { node: string; handle?: string };
}

/** Add a node of `kind`, linked after the tail (a trigger links before the current start). */
export function appendNode(graph: BuilderGraph, kind: PaletteKind, data?: JsonObject, opts: AppendOptions = {}): BuilderGraph {
  const payload = clone(data ?? defaultNodeData(kind));
  if (kind === TRIGGER_NODE_TYPE) {
    if (hasTrigger(graph)) return graph;
    const head = chainStart(graph);
    const trigger: BuilderNode = {
      id: TRIGGER_NODE_ID,
      type: TRIGGER_NODE_TYPE,
      position: layoutPosition(0),
      isStart: true,
      width: LAYOUT.nodeWidth,
      height: LAYOUT.nodeHeight,
      data: payload
    };
    const edges = head ? [chainEdge(TRIGGER_NODE_ID, head), ...graph.edges] : graph.edges;
    return autoLayout({ ...graph, nodes: [trigger, ...graph.nodes], edges, hasNodes: graph.hasNodes });
  }

  const id = newNodeId(graph);
  const from = opts.after?.node ?? chainTail(graph);
  const fromNode = from ? graph.nodes.find((n) => n.id === from) : undefined;
  const handle = opts.after?.handle;

  let edges = graph.edges;
  if (fromNode) {
    // One link per port: replace whatever left this port before.
    edges = edges.filter((e) => !(e.source === fromNode.id && (e.sourceHandle ?? undefined) === handle));
    edges = [...edges, handle ? branchEdge(fromNode.id, id, handle) : chainEdge(fromNode.id, id)];
  }

  const position = opts.at ?? (fromNode
    ? { x: fromNode.position.x + (handle ? portOffset(fromNode, handle) : 0), y: fromNode.position.y + LAYOUT.nodeHeight + LAYOUT.rowGap }
    : layoutPosition(0, graph.nodes.length));

  const node: BuilderNode = { id, type: kind, position, width: LAYOUT.nodeWidth, height: LAYOUT.nodeHeight, data: payload };
  const next: BuilderGraph = { ...graph, nodes: [...graph.nodes, node], edges, hasNodes: true };
  return nudgeApart(next, id);
}

/** Horizontal offset for a branch child so siblings fan out under their port. */
function portOffset(node: BuilderNode, handle: string): number {
  const outputs = outputsFor(node);
  const i = outputs.findIndex((o) => o.id === handle);
  if (i < 0 || outputs.length <= 1) return 0;
  return (i - (outputs.length - 1) / 2) * (LAYOUT.nodeWidth + LAYOUT.gapX);
}

/** Move a freshly placed node right until it overlaps nothing. */
function nudgeApart(graph: BuilderGraph, id: string): BuilderGraph {
  const me = graph.nodes.find((n) => n.id === id);
  if (!me) return graph;
  const overlaps = (a: BuilderNode, b: BuilderNode) =>
    Math.abs(a.position.x - b.position.x) < (LAYOUT.nodeWidth + LAYOUT.gapX) * 0.9
    && Math.abs(a.position.y - b.position.y) < (LAYOUT.nodeHeight + LAYOUT.rowGap) * 0.6;
  let position = { ...me.position };
  let guard = 0;
  while (graph.nodes.some((n) => n.id !== id && overlaps({ ...me, position }, n)) && guard++ < 20) {
    position = { x: position.x + LAYOUT.nodeWidth + LAYOUT.gapX, y: position.y };
  }
  return { ...graph, nodes: graph.nodes.map((n) => (n.id === id ? { ...n, position } : n)) };
}

export type ConnectError = 'self' | 'into-trigger' | 'cycle' | 'unknown-node' | 'unknown-port';

/**
 * Link `source[port] → target`, replacing whatever left that port before.
 * Rejects self-links, links into the trigger, unknown ports and loops.
 */
export function connect(graph: BuilderGraph, source: string, target: string, sourceHandle?: string): { graph: BuilderGraph; error?: ConnectError } {
  if (source === target) return { graph, error: 'self' }; // i18n-ignore: ConnectError code, mapped to copy by connectMessage()
  if (target === TRIGGER_NODE_ID) return { graph, error: 'into-trigger' };
  const src = graph.nodes.find((n) => n.id === source);
  if (!src || !graph.nodes.some((n) => n.id === target)) return { graph, error: 'unknown-node' };
  const ports = outputsFor(src);
  const handle = sourceHandle || undefined;
  if (handle && !ports.some((p) => p.id === handle)) return { graph, error: 'unknown-port' };
  if (!handle && ports.length > 1) {
    // A branching node has no default way out: the first port stands in.
    return connect(graph, source, target, ports[0].id);
  }
  const edges = graph.edges.filter((e) => !(e.source === source && (e.sourceHandle ?? undefined) === handle));
  const candidate: BuilderGraph = { ...graph, edges: [...edges, handle ? branchEdge(source, target, handle) : chainEdge(source, target)] };
  if (reachableFrom(candidate, target).has(source)) return { graph, error: 'cycle' }; // i18n-ignore: ConnectError code
  return { graph: candidate };
}

/** Remove a node; a node with one way in and one default way out is spliced out (prev → next). */
export function removeNode(graph: BuilderGraph, id: string): BuilderGraph {
  if (!graph.nodes.some((n) => n.id === id)) return graph;
  const incoming = incomingOf(graph, id);
  const outgoing = outgoingOf(graph, id);
  const edges = graph.edges.filter((e) => e.source !== id && e.target !== id);
  if (incoming.length === 1 && outgoing.length === 1 && !outgoing[0].sourceHandle) {
    const prev = incoming[0];
    const next = outgoing[0].target;
    if (!edges.some((e) => e.source === prev.source && (e.sourceHandle ?? undefined) === (prev.sourceHandle ?? undefined))) {
      edges.push(prev.sourceHandle ? branchEdge(prev.source, next, prev.sourceHandle) : chainEdge(prev.source, next));
    }
  }
  return { ...graph, nodes: graph.nodes.filter((n) => n.id !== id), edges };
}

/** A copy of a node beside the original, unlinked, with the same fields. */
export function duplicateNode(graph: BuilderGraph, id: string): BuilderGraph {
  const source = graph.nodes.find((n) => n.id === id);
  if (!source || source.id === TRIGGER_NODE_ID) return graph;
  const copy: BuilderNode = {
    ...source,
    id: newNodeId(graph),
    position: { x: source.position.x + 40, y: source.position.y + 40 },
    data: clone(source.data)
  };
  delete copy.data.id;
  // The copy is a new step: the original's saved sign-in secrets stay with the original.
  if (copy.data.auth && typeof copy.data.auth === 'object' && !Array.isArray(copy.data.auth)) delete (copy.data.auth as Record<string, unknown>).saved;
  return nudgeApart({ ...graph, nodes: [...graph.nodes, copy], hasNodes: true }, copy.id);
}

export function removeEdge(graph: BuilderGraph, edgeId: string): BuilderGraph {
  if (!graph.edges.some((e) => e.id === edgeId)) return graph;
  return { ...graph, edges: graph.edges.filter((e) => e.id !== edgeId) };
}

/**
 * Change a node's fields. Ports are derived from the fields (an ask's options
 * are its ports), so an edge leaving a port that no longer exists is dropped.
 */
export function updateNodeData(graph: BuilderGraph, id: string, data: JsonObject): BuilderGraph {
  const nodes = graph.nodes.map((n) => (n.id === id ? { ...n, data: clone(data) } : n));
  const node = nodes.find((n) => n.id === id);
  if (!node) return { ...graph, nodes };
  const ports = new Set(outputsFor(node).map((p) => p.id));
  const single = ports.size <= 1;
  const edges = graph.edges.filter((e) => {
    if (e.source !== id) return true;
    if (!e.sourceHandle) return single;
    return ports.has(e.sourceHandle);
  });
  return { ...graph, nodes, edges };
}

/** Replace node positions from the canvas (drags) without touching structure. */
export function withNodes(graph: BuilderGraph, nodes: BuilderNode[]): BuilderGraph {
  return { ...graph, nodes };
}

/** One thing wrong with a flow, as a catalog key plus its interpolation values. */
export interface GraphProblem {
  key: string;
  vars?: Record<string, string | number>;
  /** The node it concerns, so the console can select it. */
  nodeId?: string;
  /** `warning` runs but does something unhelpful; `error` will not run as meant. */
  severity?: 'warning' | 'error';
}

/** A flow of the business, as a Run another flow step needs to know it. */
export interface FlowRef {
  id: string;
  name: string;
  is_active: boolean;
}

/** As the Run another flow step's own check (catalog.ts): anything else is "choose the flow to run". */
const UUID = /^[0-9a-f-]{36}$/i;
const LETTER_OR_DIGIT = /^[\p{L}\p{Nd}]$/u;

/**
 * The engine's slug for a button without an id (FlowEngine.Slug(title, index)): lowercase, every character that is not
 * a letter or a digit becomes `_` (one each, in UTF-16 units, as .NET walks a string), `_` trimmed from both ends,
 * `opt{index}` when nothing is left, at most 40 characters. Not the builder's own `slug` in catalog.ts, which folds a
 * run of other characters into one `_` and keeps only a to z.
 */
export function engineSlug(title: string, index: number): string {
  const lower = title.toLowerCase();
  let out = '';
  for (let i = 0; i < lower.length; i++) out += LETTER_OR_DIGIT.test(lower[i]) ? lower[i] : '_';
  const s = out.replace(/^_+|_+$/g, '');
  return s.length === 0 ? `opt${index}` : s.slice(0, 40);
}

/**
 * A menu step's buttons as the engine names them when a customer taps one (FlowEngine.MenuOptions): `options`, else
 * `buttons`, else `list.options`; each button's `id` (else `value`) as written, else the slug of its title with its
 * 1-based place. A button with no title is never sent.
 */
export function menuButtons(data: JsonObject): Array<{ id: string; title: string }> {
  const list = data.list && typeof data.list === 'object' && !Array.isArray(data.list) ? (data.list as JsonObject).options : undefined;
  const raw = Array.isArray(data.options) ? data.options : Array.isArray(data.buttons) ? data.buttons : Array.isArray(list) ? list : [];
  const out: Array<{ id: string; title: string }> = [];
  raw.forEach((o, i) => {
    const obj = o && typeof o === 'object' && !Array.isArray(o) ? (o as JsonObject) : null;
    const rawTitle = obj ? (obj.title ?? obj.label) : o;
    const title = rawTitle === null || rawTitle === undefined ? '' : String(rawTitle);
    if (!title.trim()) return;
    const rawId = obj ? (obj.id ?? obj.value) : undefined;
    const id = rawId === null || rawId === undefined ? '' : String(rawId);
    out.push({ id: id.trim() ? id : engineSlug(title, i + 1), title: title.trim() });
  });
  return out;
}

/**
 * Checks a flow will actually do something when it runs. Every problem here
 * describes a flow that RUNS and does nothing useful, which is far harder to
 * diagnose than one that fails outright.
 *
 * `flows` (2 November 2026), the business's flows once loaded: a Run another flow step that opens one that was
 * deleted or is switched off is named, as the loose-ends check names it on the server.
 */
export function validateGraph(graph: BuilderGraph, opts: { channels?: readonly string[]; flows?: readonly FlowRef[] } = {}): GraphProblem[] {
  const problems: GraphProblem[] = [];
  // The channels this flow runs on: the ones its trigger names, and any the
  // console knows of besides (a USSD code given to the flow's bot).
  const channels = [...triggerChannels(graph), ...(opts.channels ?? [])];

  if (!hasTrigger(graph)) problems.push({ key: 'flows.validate.noTrigger', severity: 'error' });

  for (const id of unlinkedNodeIds(graph)) {
    problems.push({ key: 'flows.validate.unlinkedNode', nodeId: id, vars: { id: label(graph, id) }, severity: 'warning' });
  }

  const engineNodes = graph.nodes.filter((n) => n.id !== TRIGGER_NODE_ID);
  if (engineNodes.length > 0 && !engineNodes.some((n) => ['send', 'template', 'reply', 'ask', 'end', 'handoff', 'llm', 'language'].includes(n.type))) {
    // A flow that never talks to the customer runs to completion in silence.
    problems.push({ key: 'flows.validate.noReply', severity: 'warning' });
  }

  for (const node of graph.nodes) {
    if (node.id === TRIGGER_NODE_ID) {
      const d = node.data;
      const keywords = Array.isArray(d.keywords) ? d.keywords : typeof d.keyword === 'string' && d.keyword ? [d.keyword] : [];
      // No keyword is fine when the flow says what it is for: being asked for starts it.
      if ((d.type === 'keyword' || d.type === undefined) && keywords.length === 0 && !intentEnabled(d)) {
        problems.push({ key: 'flows.validate.triggerNoKeyword', nodeId: node.id, severity: 'error' });
      }
      continue;
    }
    const spec = NODE_KINDS[node.type];
    if (spec?.validate) {
      for (const p of spec.validate(node.data)) {
        problems.push({ ...p, nodeId: node.id, vars: { ...(p.vars ?? {}), id: label(graph, node.id) } });
      }
    }
    // A step the flow's channel cannot run (USSD, 4 October 2026): warned, never hidden.
    for (const p of channelProblems(node.type, node.data, channels)) {
      problems.push({ ...p, nodeId: node.id, vars: { ...(p.vars ?? {}), id: label(graph, node.id) } });
    }
    // Run another flow into a flow that was deleted or is switched off (2 November 2026): SJM's improved copies opened
    // deleted originals. The customer is told it is not available; the flow chosen by mistake is what to fix.
    if (node.type === 'flow' && opts.flows) {
      const target = typeof node.data.flow_id === 'string' ? node.data.flow_id.trim() : '';
      if (UUID.test(target)) {
        const opened = opts.flows.find((f) => f.id.toLowerCase() === target.toLowerCase());
        if (!opened) {
          problems.push({ key: 'flows.validate.flowMissing', nodeId: node.id, vars: { id: label(graph, node.id) }, severity: 'error' });
        } else if (!opened.is_active) {
          problems.push({ key: 'flows.validate.flowOff', nodeId: node.id, vars: { id: label(graph, node.id), name: opened.name }, severity: 'warning' });
        }
      }
    }
    // A branching node with a port nothing leaves from: that branch ends the
    // run silently, which is fine for "No" but is worth saying out loud.
    const ports = outputsFor(node);
    if (ports.length > 1) {
      const outgoing = outgoingOf(graph, node.id);
      // A branch-less edge is the default path for every outcome, which is
      // how a definition written by hand usually links a branching node.
      const hasDefault = outgoing.some((e) => !e.sourceHandle);
      const linked = new Set(outgoing.map((e) => e.sourceHandle ?? ''));
      const missing = ports.filter((p) => !linked.has(p.id));
      if (!hasDefault && missing.length === ports.length) {
        // Nothing leaves at all: said once for the step, not once per button as well.
        problems.push({ key: 'flows.validate.noBranchLinked', nodeId: node.id, vars: { id: label(graph, node.id) }, severity: 'warning' });
      } else if (!hasDefault && (node.type === 'ask' || node.type === 'question') && !listMenu(node.data)) {
        // A menu button the engine has nowhere to take (FlowGraph.NextOf: its own link, matched without regard to
        // case, else the step's plain link), named by the engine's id for it, as the loose-ends check on the server
        // does (2 November 2026). The No match exit is the builder's own and is not a button.
        const branches = new Set(outgoing.map((e) => (e.sourceHandle ?? '').toLowerCase()).filter(Boolean));
        for (const b of menuButtons(node.data).filter((x) => !branches.has(x.id.toLowerCase()))) {
          problems.push({ key: 'flows.validate.optionUnlinked', nodeId: node.id, vars: { id: label(graph, node.id), option: b.title }, severity: 'warning' });
        }
      } else if (!hasDefault && node.type === 'decision') {
        // A path that leads nowhere ends the chat for whoever it chose. Ending at
        // Otherwise is an ordinary choice, so that exit is not flagged.
        for (const p of missing.filter((m) => m.id !== 'otherwise')) {
          problems.push({ key: 'flows.validate.decisionPathUnlinked', nodeId: node.id, vars: { id: label(graph, node.id), path: p.label ?? p.id }, severity: 'warning' });
        }
      }
    }
  }

  return problems;
}

/** The channels the trigger names (`trigger.channels`); none means every channel. */
export function triggerChannels(graph: BuilderGraph): string[] {
  const trigger = graph.nodes.find((n) => n.id === TRIGGER_NODE_ID);
  const raw = trigger && Array.isArray(trigger.data.channels) ? trigger.data.channels : [];
  return raw.filter((c): c is string => typeof c === 'string' && c.trim() !== '').map((c) => c.trim().toLowerCase());
}

/** The node's own name, else its id: what a problem message calls it. */
/** How a message refers to a step: the name the user gave it, else its number on the canvas. */
export function label(graph: BuilderGraph, id: string): string {
  const node = graph.nodes.find((n) => n.id === id);
  const name = node && typeof node.data.name === 'string' ? node.data.name.trim() : '';
  if (name) return name;
  const n = stepNumbers(graph).get(id);
  return n ? `Step ${n}` : id; // i18n-ignore: the catalogue wraps this in a full sentence
}

/**
 * Every variable a node earlier in the flow (or the platform) can put in the
 * context, for autocomplete in the inspector: `contact.name`, `message`, an
 * ask's variable, a set's names, an http's `save_as`.
 */
export function knownVariables(graph: BuilderGraph): string[] {
  const names = new Set<string>(['message', 'last_message', 'contact.name', 'contact.msisdn', 'contact.email', 'contact.tags', 'contact.known', 'trigger.text', 'now', 'clock.date', 'clock.time', 'clock.weekday', 'channel', 'bot.name', 'bot.greeting']);
  for (const n of graph.nodes) {
    const d = n.data;
    if (n.type === 'ask' && typeof d.variable === 'string' && d.variable) {
      names.add(d.variable);
      if (Array.isArray(d.options) && d.options.length) names.add(`${d.variable}_label`);
      // A menu from a list keeps the item too; a date keeps a time said with it (19 October 2026).
      if (typeof d.options_from === 'string' && d.options_from.trim()) for (const v of ['label', 'item', 'index']) names.add(`${d.variable}_${v}`);
      if (d.validate === 'date' || d.validate === 'datetime') names.add(`${d.variable}_time`);
      if (d.validate === 'datetime') names.add(`${d.variable}_date`);
    }
    if (n.type === 'set') {
      if (Array.isArray(d.assignments)) for (const a of d.assignments) if (a && typeof a === 'object' && typeof (a as JsonObject).name === 'string') names.add(String((a as JsonObject).name));
      if (d.variables && typeof d.variables === 'object') for (const k of Object.keys(d.variables as JsonObject)) names.add(k);
    }
    if (n.type === 'form') {
      const saveAs = typeof d.save_as === 'string' && d.save_as ? d.save_as : 'form';
      names.add(saveAs);
      names.add(`${saveAs}_json`);
      if (Array.isArray(d.fields)) {
        for (const f of d.fields) {
          if (f && typeof f === 'object' && typeof (f as JsonObject).name === 'string' && (f as JsonObject).name) names.add(`${saveAs}.${String((f as JsonObject).name)}`);
        }
      }
    }
    if (n.type === 'http') {
      const saveAs = typeof d.save_as === 'string' && d.save_as ? d.save_as : 'http';
      names.add(`${saveAs}.status`);
      names.add(`${saveAs}.ok`);
      names.add(`${saveAs}.body`);
      if (Array.isArray(d.extract)) for (const e of d.extract) if (e && typeof e === 'object' && typeof (e as JsonObject).name === 'string') names.add(String((e as JsonObject).name));
    }
    if (n.type === 'llm') {
      const saveAs = typeof d.save_as === 'string' && d.save_as ? d.save_as : 'llm';
      names.add(`${saveAs}.text`);
      names.add(`${saveAs}.confidence`);
      if (d.mode === 'classify') names.add(`${saveAs}.label`);
      if (d.mode === 'extract' && Array.isArray(d.fields)) {
        for (const f of d.fields) {
          const name = f && typeof f === 'object' ? String((f as JsonObject).id ?? (f as JsonObject).name ?? '') : typeof f === 'string' ? f : '';
          if (name.trim()) names.add(name.trim());
        }
      }
    }
    if (n.type === 'booking' || n.type === 'manage_booking') {
      for (const v of ['number', 'when', 'service', 'staff', 'status']) names.add(`booking.${v}`);
    }
    if (n.type === 'ticket') {
      for (const v of ['number', 'id', 'status']) names.add(`ticket.${v}`);
    }
    // Look up (19 October 2026): its data points and what it adds, under the source's name.
    if (n.type === 'lookup') {
      const saveAs = typeof d.save_as === 'string' && d.save_as.trim() ? d.save_as.trim() : '';
      if (saveAs) {
        for (const v of ['text', 'count', 'found', 'items']) names.add(`${saveAs}.${v}`);
        if (Array.isArray(d.points)) for (const p of d.points) if (typeof p === 'string' && p) names.add(`${saveAs}.${p}`);
      }
    }
    if (n.type === 'ticket_status') {
      for (const v of ['number', 'subject', 'status', 'client_label']) names.add(`ticket.${v}`);
    }
    // Change language (3 November 2026): the language chosen, by its own name and its code.
    if (n.type === 'language') {
      names.add('chat_language.name');
      names.add('chat_language.code');
    }
    if (n.type === 'sms') {
      names.add('sms.ok');
    }
    if (n.type === 'email') {
      names.add('email.ok');
      names.add('email.detail');
    }
    if (n.type === 'hours') {
      names.add('business_hours.open');
      names.add('business_hours.local_time');
    }
    if (n.type === 'contact' && d.fields && typeof d.fields === 'object') {
      for (const k of Object.keys(d.fields as JsonObject)) names.add(`contact.fields.${k}`);
    }
  }
  return [...names].sort();
}
